import { InjectionToken } from '@angular/core';
import { Route } from '@angular/router';
import { SideNavigationLink } from '@vendasta/atlas';
import { Observable, map, switchMap, combineLatest } from 'rxjs';
import { BusinessNavDataService } from '../../data.service';
import { FeatureFlagService } from '@galaxy/partner';
import { BusinessNavConfigService } from '../../config.service';
import { shareReplay } from 'rxjs/operators';

export const PAGE_SUBLINKS_ID_TOKEN = new InjectionToken<string>('PAGE_SUBLINKS_ID');
export const PAGE_SUBLINKS_CONFIG_TOKEN = new InjectionToken<Observable<PageSublinksConfig>>('PAGE_SUBLINKS_CONFIG');

export type PageSublinksConfig = {
  sublinks: SideNavigationLink[];
};

/*
  Add providers to a route to provide the sublinks for the page.
*/
export function pageSublinksRoute(navigationId: string, Overrides: Route): Route {
  const routeDefaults: Partial<Route> = {
    providers: [
      { provide: PAGE_SUBLINKS_ID_TOKEN, useValue: navigationId },
      {
        provide: PAGE_SUBLINKS_CONFIG_TOKEN,
        useFactory: configFactory,
        deps: [BusinessNavDataService, PAGE_SUBLINKS_ID_TOKEN, FeatureFlagService, BusinessNavConfigService],
      },
    ],
  };

  const route: Route = {
    ...routeDefaults,
    ...Overrides,
  };

  for (const key in Overrides) {
    if (Object.prototype.hasOwnProperty.call(Overrides, key) && Array.isArray(Overrides[key])) {
      route[key] = [...(routeDefaults[key] || []), ...(Overrides[key] || [])];
    }
  }

  return route;
}

function configFactory(
  navigationService: BusinessNavDataService,
  navigationId: string,
  featureFlagService: FeatureFlagService,
  configService: BusinessNavConfigService,
): Observable<PageSublinksConfig> {
  // PHNX-984 - Clean up route_to_new_integrations flag once all partners are routed to new integrations page. (settings/integrations)
  // Route to new integrations only if flag is true else route user to old page (settings/connections)
  const routeToNewIntegrations$ = configService.config$.pipe(
    switchMap((config) =>
      featureFlagService.batchGetStatus(config.partnerId, config.marketId, ['route_to_new_integrations']),
    ),
    map((flags) => flags['route_to_new_integrations']),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  return combineLatest([navigationService.linksWithSublinks$, routeToNewIntegrations$]).pipe(
    map(([links, routeToNewIntegrations]) => {
      const link = links.find((c) => c.navigationId === navigationId);
      const sublinks = link?.subLinks ?? [];
      return sublinks.filter(
        (link) => !link.userRequired && !(link.navigationId === 'nav-social-connections' && routeToNewIntegrations),
      );
    }),
    map((sublinks) => {
      return { sublinks };
    }),
  );
}
